import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography } from 'antd';
import cn from 'classnames';

import { ReactComponent as CheckIcon } from 'assets/icons/check.svg';

import styles from './index.module.scss';

const MIN_PASSWORD_LENGTH = 8;

type CreateAccountPasswordProps = {
	value?: string;
};

const PASSWORD_INCLUDES = [
	{
		text: 'passwordMessageOneText',
		rule: (v: string | undefined) => v && /[a-z]/g.test(v),
	},
	{
		text: 'passwordMessageTwoText',
		rule: (v: string | undefined) => v && v.length >= MIN_PASSWORD_LENGTH,
	},
	{
		text: 'passwordMessageThreeText',
		rule: (v: string | undefined) => v && /[A-Z]/g.test(v),
	},
	{
		text: 'passwordMessageFourText',
		rule: (v: string | undefined) => v && /[0-9]/g.test(v),
	},
];

const STRENGTHS = ['poorText', 'weakText', 'reliableText', 'strongText'];

export const CreateAccountPassword: FC<CreateAccountPasswordProps> = ({ value = '' }) => {
	const { t } = useTranslation();

	const countOfValidRules = PASSWORD_INCLUDES.reduce(
		(acc, { rule }) => acc + (rule(value) ? 1 : 0),
		0,
	);

	let strength = 0;

	if (countOfValidRules === 4 && value.length > MIN_PASSWORD_LENGTH) {
		strength = 4;
	} else if (countOfValidRules === 4 && value.length === MIN_PASSWORD_LENGTH) {
		strength = 3;
	} else if (countOfValidRules === 3 || countOfValidRules === 2) {
		strength = 2;
	} else {
		strength = countOfValidRules;
	}
	return (
		<div className={styles.passwordValues}>
			<div>
				<Typography.Text className={styles.includesTitle}>
					{t('passwordIncludeText')}
				</Typography.Text>

				{PASSWORD_INCLUDES.map((item) => {
					const isValidRule = item.rule(value);

					return (
						<Typography.Text
							className={styles.includes}
							type={isValidRule ? 'success' : 'secondary'}
							key={item.text}
						>
							<CheckIcon
								className={cn(styles.icon, {
									[styles.iconSuccess]: isValidRule,
								})}
							/>
							{t(item.text)}
						</Typography.Text>
					);
				})}
			</div>
			<div>
				<Typography.Text className={styles.includesTitle}>
					{t('passwordStrengthText')}
					{strength > 0 && ':'} {t(STRENGTHS[strength - 1])}
				</Typography.Text>

				<div
					className={cn(styles.strengths, {
						[styles.poor]: strength === 1,
						[styles.weak]: strength === 2,
						[styles.reliable]: strength === 3,
						[styles.strong]: strength === 4,
					})}
				>
					{STRENGTHS.map((item, index) => {
						return (
							<div
								key={item}
								className={cn(styles.strengthItem, {
									[styles.active]: index < strength,
								})}
							/>
						);
					})}
				</div>
			</div>
		</div>
	);
};
